import { API_URL } from '../app/constants'

export const fetchUserExams = async (level, section) => {
    try {
      const jwt = localStorage.getItem('jwt');
      const response = await fetch(`${API_URL}/api/v1/panel/get-user-exams`, {
        method: "POST",
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ level, section })
      });
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("Error fetching exams:", error);
      throw error;
    }
  };
  
  export const initiateCheckoutSession = async () => {
    try {
      const jwt = localStorage.getItem('jwt');
      const response = await fetch(`${API_URL}/api/v1/payment/create-checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({})
      });
      return response.json();
    } catch (error) {
      console.error('Failed to initiate checkout:', error);
      throw error;
    }
  };

export const sendQuestionChat = async (questionContext, userInput, conversationHistory = null) => {
  try {
    const jwt = localStorage.getItem('jwt');
    
    // Check if we're in a personalized exam context (after answering questions)
    // Force using the new API endpoint if user_answer exists (even if null)
    const isPersonalizedExam = questionContext.hasOwnProperty('user_answer');
    
    console.log('Question context:', questionContext);
    console.log('Is personalized exam:', isPersonalizedExam);
    console.log('User answer:', questionContext.user_answer);
    
    // Use the proper endpoint based on context
    const endpoint = isPersonalizedExam 
      ? `${API_URL}/api/v1/genai/ask_question_after_answer`
      : `${API_URL}/api/v1/panel/ask-mina-help`;
      
    console.log(`Using API endpoint: ${endpoint}`);
    
    let requestBody;
    
    if (isPersonalizedExam) {
      // Format for the genai/ask_question_after_answer endpoint - expects an array
      // If there are related questions (all_questions array), send all of them
      if (questionContext.all_questions && questionContext.all_questions.length > 1) {
        requestBody = questionContext.all_questions.map(q => ({
          question_id: q.question_id,
          user_answer: q.user_answer,
          user_input: userInput,
          conversation_history: conversationHistory || []
        }));
      } else {
        // Single question
        requestBody = [
          {
            question_id: questionContext.question_id,
            user_answer: questionContext.user_answer,
            user_input: userInput,
            conversation_history: conversationHistory || []
          }
        ];
      }
    } else {
      // Format for the original ask-mina-help endpoint
      requestBody = {
        question_id: questionContext.question_id,
        question_text: questionContext.question_text,
        options: questionContext.options,
        correct_answer: questionContext.correct_answer,
        explanation: questionContext.explanation || '',
        user_input: `For the question: "${questionContext.question_text}" with correct answer "${questionContext.correct_answer}", ${userInput}`
      };
    }
    
    console.log('Request body being sent:', JSON.stringify(requestBody, null, 2));
    
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error:', response.status, errorText);
      throw new Error(`Failed to get response from the server: ${response.status} - ${errorText}`);
    }
    
    // Handle streaming response
    if (isPersonalizedExam) {
      // For the streaming /ask_question_after_answer endpoint
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullResponse = '';
      
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          const chunk = decoder.decode(value, { stream: true });
          fullResponse += chunk;
        }
        
        console.log('Streaming response received:', fullResponse);
        
        // Return the streaming response in the expected format
        return {
          text: fullResponse,
          choices: [{
            message: {
              content: fullResponse
            }
          }]
        };
      } catch (streamError) {
        console.error('Error reading stream:', streamError);
        throw new Error('Failed to read streaming response');
      }
    } else {
      // Handle non-streaming response for the original endpoint
      const responseText = await response.text();
      
      // Check if the response text contains a JSON part (usually after the text part)
      const jsonStartIndex = responseText.indexOf('{');
      
      if (jsonStartIndex >= 0) {
        try {
          // Try to extract and parse the JSON part
          const jsonPart = responseText.substring(jsonStartIndex);
          const jsonData = JSON.parse(jsonPart);
          
          // Also extract the text part that came before the JSON (if any)
          const textPart = jsonStartIndex > 0 ? responseText.substring(0, jsonStartIndex).trim() : '';
          
          // Return the combined data
          return {
            text: textPart,
            history: jsonData.history || [],
            // Also provide in OpenAI-compatible format for the existing handler
            choices: [{
              message: {
                content: textPart
              }
            }]
          };
        } catch (e) {
          console.error('Failed to parse JSON part of response:', e);
        }
      }
      
      // If JSON parsing failed or there was no JSON, return the text as is
      return {
        text: responseText,
        choices: [{
          message: {
            content: responseText
          }
        }]
      };
    }
  } catch (error) {
    console.error('Error in sendQuestionChat:', error);
    throw error;
  }
};

export const sendQuestionQuery = async (questionContext, userQuery) => {
  try {
    const jwt = localStorage.getItem('jwt');
    const response = await fetch(`${API_URL}/api/v1/panel/ask-question`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        question_id: questionContext.question_id,
        question_text: questionContext.question_text,
        options: questionContext.options,
        correct_answer: questionContext.correct_answer,
        explanation: questionContext.explanation,
        user_input: userQuery
      })
    });
    
    if (!response.ok) {
      throw new Error('Failed to get response from the server');
    }
    
    return response.json();
  } catch (error) {
    console.error('Error in sendQuestionQuery:', error);
    throw error;
  }
};

// Get user's streak information
export const getUserStreak = async () => {
  try {
    const jwt = localStorage.getItem('jwt');
    // Use the proxy defined in setupProxy.js to avoid CORS issues during development
    const response = await fetch('/api/panel/user-streak', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json',
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to get streak information');
    }
    
    return response.json();
  } catch (error) {
    console.error('Error fetching user streak:', error);
    return { streak_count: 0, last_quiz_date: null };
  }
};